import { useId } from 'react';

interface BoltRatingProps {
  rating?: number;
  maxBolts?: number;
  color?: string;
  defaultColor?:string;
}

// Convert hex color to RGBA with specified alpha
const hexToRgba = (hex: string, alpha: number = 1): string => {
  const cleanHex = hex.replace(/^#/, '');
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BoltRating: React.FC<BoltRatingProps> = ({
  rating = 0,
  maxBolts = 5,
  color = '#16A34A',
  defaultColor
}) => {
  const uniqueId = useId();
  const roundedRating = Number(rating.toFixed(1));
  const fullBolts = Math.floor(roundedRating);
  const decimalPart = Number((roundedRating % 1).toFixed(1));
  const hasPartialBolt = decimalPart > 0;
  const emptyBolts = maxBolts - Math.ceil(roundedRating);
  const backgroundColor = defaultColor || hexToRgba(color, 0.3);

  return (
    <div className="flex items-center space-x-1">
      {[...Array(fullBolts)].map((_, index) => (
        <svg
          key={`full-${index}`}
          className="w-3 h-[15px]"
          viewBox="0 0 12 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_4540_4103)">
            <path
              d="M9.35885 1.71953C9.51689 1.34493 9.39903 0.907425 9.07492 0.6668C8.75082 0.426175 8.30885 0.44805 8.00617 0.716019L1.14903 6.84102C0.881175 7.08164 0.784746 7.46719 0.910639 7.80625C1.03653 8.14532 1.35796 8.375 1.71421 8.375H4.70082L2.641 13.2805C2.48296 13.6551 2.60082 14.0926 2.92492 14.3332C3.24903 14.5738 3.691 14.552 3.99367 14.284L10.8508 8.15899C11.1187 7.91836 11.2151 7.53282 11.0892 7.19375C10.9633 6.85469 10.6446 6.62774 10.2856 6.62774H7.29903L9.35885 1.71953Z"
              fill={color}
            />
          </g>
          <defs>
            <clipPath id="clip0_4540_4103">
              <rect width="12" height="14" fill="white" transform="translate(0 0.5)" />
            </clipPath>
          </defs>
        </svg>
      ))}
      {hasPartialBolt && (
        <svg
          className="w-3 h-[15px]"
          viewBox="0 0 12 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id={`partial-bolt-${uniqueId}`}>
              <stop offset={`${decimalPart * 100}%`} stopColor={color} />
              <stop offset={`${decimalPart * 100}%`} stopColor={backgroundColor} />
            </linearGradient>
          </defs>
          <g clipPath="url(#clip0_4540_4103)">
            <path
              d="M9.35885 1.71953C9.51689 1.34493 9.39903 0.907425 9.07492 0.6668C8.75082 0.426175 8.30885 0.44805 8.00617 0.716019L1.14903 6.84102C0.881175 7.08164 0.784746 7.46719 0.910639 7.80625C1.03653 8.14532 1.35796 8.375 1.71421 8.375H4.70082L2.641 13.2805C2.48296 13.6551 2.60082 14.0926 2.92492 14.3332C3.24903 14.5738 3.691 14.552 3.99367 14.284L10.8508 8.15899C11.1187 7.91836 11.2151 7.53282 11.0892 7.19375C10.9633 6.85469 10.6446 6.62774 10.2856 6.62774H7.29903L9.35885 1.71953Z"
              fill={`url(#partial-bolt-${uniqueId})`}
            />
          </g>
          <g clipPath="url(#clip0_4540_4107)">
            <path
              d="M9.35885 1.71953C9.51689 1.34493 9.39903 0.907425 9.07492 0.6668C8.75082 0.426175 8.30885 0.44805 8.00617 0.716019L1.14903 6.84102C0.881175 7.08164 0.784746 7.46719 0.910639 7.80625C1.03653 8.14532 1.35796 8.375 1.71421 8.375H4.70082L2.641 13.2805C2.48296 13.6551 2.60082 14.0926 2.92492 14.3332C3.24903 14.5738 3.691 14.552 3.99367 14.284L10.8508 8.15899C11.1187 7.91836 11.2151 7.53282 11.0892 7.19375C10.9633 6.85469 10.6446 6.62774 10.2856 6.62774H7.29903L9.35885 1.71953Z"
              fill={backgroundColor}
            />
          </g>
          <defs>
            <clipPath id="clip0_4540_4103">
              <rect width="12" height="14" fill="white" transform="translate(0 0.5)" />
            </clipPath>
            <clipPath id="clip0_4540_4107">
              <rect width="12" height="14" fill="white" transform="translate(0 0.5)" />
            </clipPath>
          </defs>
        </svg>
      )}
      {[...Array(emptyBolts)].map((_, index) => (
        <svg
          key={`empty-${index}`}
          className="w-3 h-[15px]"
          viewBox="0 0 12 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_4540_4107)">
            <path
              d="M9.35885 1.71953C9.51689 1.34493 9.39903 0.907425 9.07492 0.6668C8.75082 0.426175 8.30885 0.44805 8.00617 0.716019L1.14903 6.84102C0.881175 7.08164 0.784746 7.46719 0.910639 7.80625C1.03653 8.14532 1.35796 8.375 1.71421 8.375H4.70082L2.641 13.2805C2.48296 13.6551 2.60082 14.0926 2.92492 14.3332C3.24903 14.5738 3.691 14.552 3.99367 14.284L10.8508 8.15899C11.1187 7.91836 11.2151 7.53282 11.0892 7.19375C10.9633 6.85469 10.6446 6.62774 10.2856 6.62774H7.29903L9.35885 1.71953Z"
              fill={backgroundColor}
            />
          </g>
          <defs>
            <clipPath id="clip0_4540_4107">
              <rect width="12" height="14" fill="white" transform="translate(0 0.5)" />
            </clipPath>
          </defs>
        </svg>
      ))}
      {/* <span className="ml-2 text-gray-600">{roundedRating.toFixed(1)}</span> */}
    </div>
  );
};

export default BoltRating;